import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { QuizQuestionData } from "../constants/quizQuestionData";
import { QuizResponse } from "../types/QuizSubmission";
import QuizQuestion from "./QuizQuestion";
import toast, { Toaster } from "react-hot-toast";
import "../css/QuizFeedback.css";
import "../css/QuizQuestion.css";
import "../css/Main.css";

interface QuizFeedbackProps {
  questions: QuizQuestionData[];
  responses: QuizResponse[];
}

const QuizFeedback: React.FC<QuizFeedbackProps> = ({
  questions,
  responses,
}) => {
  const [shareableResults, setShareableResults] = React.useState("");
  const navigate = useNavigate();

  const calculateScore = () => {
    let score = 0;
    responses.forEach((response, index) => {
      const correctAnswers = questions[index].subQuestions.map(
        (subQuestion) => subQuestion.correct_answer
      );
      if (
        JSON.stringify(response.selectedAnswers) ===
        JSON.stringify(correctAnswers)
      ) {
        score++;
      }
    });
    return score;
  };

  const score = calculateScore();

  const generateShareableText = () => {
    const websiteUrl =
      window.location.origin.replace(/^http(s?):\/\//i, "") +
      import.meta.env.BASE_URL;
    const scoreText = `I won ${score} car${
      score !== 1 ? "s" : ""
    } in Goat or Glory: Monty's Gauntlet!\n\n`;

    const questionResults = responses.map((response, index) => {
      const correctAnswers = questions[index].subQuestions.map(
        (subQuestion) => subQuestion.correct_answer
      );
      const isCorrect =
        JSON.stringify(response.selectedAnswers) ===
        JSON.stringify(correctAnswers);
      return isCorrect ? "🚗" : "🐐";
    });

    const doorsText = "🚪".repeat(questions.length) + "\n";
    const resultsText = questionResults.join("") + "\n\n";

    return `${scoreText}${doorsText}${resultsText}${websiteUrl}`;
  };

  const handleShare = () => {
    const text = generateShareableText();
    try {
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success("Results copied to clipboard! 🎉"))
        .catch(() => toast.error("Failed to copy results"));
    } catch {
      toast.error("Failed to copy results");
    }
    setShareableResults(text);
  };

  const handleTryAgain = () => {
    // Clear all localStorage values related to quiz state
    localStorage.removeItem("currentQuestionIndex");
    localStorage.removeItem("allSelectedAnswers");
    localStorage.removeItem("submittedQuestions");

    // Navigate to home page
    navigate("/");
  };

  return (
    <div className="quiz-feedback">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2000,
          style: {
            background: "#fff",
            color: "#333",
          },
        }}
      />
      <div className="question-title content-container">
        <h3 style={{}}>
          Your Score: {score} / {questions.length}
        </h3>
        <div className="buttons-container">
          <button onClick={handleShare} className="share-button">
            Copy Results
          </button>
          <button onClick={handleTryAgain} className="try-again-button">
            Try Again
          </button>
        </div>
        {shareableResults && (
          <p className="shareable-results">{shareableResults}</p>
        )}
        <hr style={{ width: "50%", marginBottom: -5 }}/>
        <Link to="/llm-results" className="llm-results-cta">
          Curious how your favorite LLM did?
        </Link>
      </div>

      {questions.map((question, index) => {
        const response = responses[index];
        return (
          <div key={index} className="quiz-container content-container">
            <h4 className="feedback-title">{question.title}</h4>
            <hr style={{ width: "50%", marginBottom: 0 }} />
            <QuizQuestion
              key={question.identifier}
              question={question}
              selectedAnswers={response.selectedAnswers}
              showFeedback={true}
              isFinal={true}
            />
          </div>
        );
      })}
    </div>
  );
};

export default QuizFeedback;
